import { computed, type ComputedRef } from 'vue'

import { useAppContentStore } from '@/stores/appContent'
import { useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'

import { getTextContentItemValueFromContentModule, substitute } from '@/composables/contentful'
import { formatWholeDollarCurrency } from '@/lib/utils'

import { type RewardTier, RewardTierState } from '@/types/RewardTier'

import type { ContentfulContentModule } from '#contentful/ContentfulContentModule'

// Content-item identifiers in the `rewards-tracker` content module, with in-code fallbacks so the
// headings still render (and stay grep-able) if the module is absent during rollout.
const HEADINGS = {
  empty: {
    id: 'rewards-tracker-heading-empty',
    fallback: '✨ Select a package to **start unlocking rewards!**'
  },
  inProgress: {
    id: 'rewards-tracker-heading-in-progress',
    fallback: "🎉 You're only **##AmountToNext##** away from unlocking **##NextRewardName##**!"
  },
  tierUnlocked: {
    id: 'rewards-tracker-heading-tier-unlocked',
    fallback:
      '🎉 **##LastUnlockedRewardName## Unlocked!** Add **##AmountToNext##** more for **##NextRewardName##**!'
  },
  allUnlocked: {
    id: 'rewards-tracker-heading-all-unlocked',
    fallback:
      "🎊 **Incredible!** You've unlocked all rewards, including your **##LastRewardName##**."
  }
} as const

// The footer call-out is its own minimal amount-to-next string (distinct from the top-of-page
// heading): always the "away from the next reward" form, hidden once all rewards are unlocked.
const FOOTER_CALLOUT = {
  id: 'rewards-tracker-footer-callout',
  // Generic reward icon (not tier-specific) — the next reward varies per ladder/state.
  fallback: "🎁 You're only **##AmountToNext##** away from **##NextRewardName##**!"
} as const

/**
 * Resolves the tracker heading for a ladder + effective subtotal: picks the per-state template from
 * the `rewards-tracker` content module (with in-code fallback) and substitutes the `##…##` markers.
 * Pure (no store access) so the debug harness can drive it at sample subtotals; `useRewards` calls it
 * with the live cart subtotal.
 */
export function buildRewardsHeading(
  tiers: RewardTier[],
  effectiveSubtotal: number,
  rewardsTrackerModule: ContentfulContentModule | null
): string {
  if (tiers.length === 0) {
    return ''
  }
  const read = (heading: { id: string; fallback: string }): string =>
    getTextContentItemValueFromContentModule(rewardsTrackerModule, heading.id, heading.fallback) ??
    heading.fallback

  const unlockedCount = tiers.filter((t) => t.state === RewardTierState.Unlocked).length

  // All unlocked.
  if (unlockedCount === tiers.length) {
    const last = tiers[tiers.length - 1]
    return substitute(read(HEADINGS.allUnlocked), { LastRewardName: last.label })
  }

  const current = tiers.find((t) => t.state === RewardTierState.Current)
  const vars = {
    AmountToNext: current ? formatWholeDollarCurrency(current.amountToUnlock) : '',
    NextRewardName: current?.label ?? ''
  }

  // Nothing unlocked yet: empty cart vs. in-progress toward the first tier.
  if (unlockedCount === 0) {
    return effectiveSubtotal <= 0
      ? read(HEADINGS.empty)
      : substitute(read(HEADINGS.inProgress), vars)
  }

  // At least one tier crossed, more to go. Scan from the top for the highest unlocked tier (no array
  // copy; `findLast` isn't in the ES2020 lib target).
  let lastUnlocked: RewardTier | undefined
  for (let i = tiers.length - 1; i >= 0; i--) {
    if (tiers[i].state === RewardTierState.Unlocked) {
      lastUnlocked = tiers[i]
      break
    }
  }
  return substitute(read(HEADINGS.tierUnlocked), {
    ...vars,
    LastUnlockedRewardName: lastUnlocked?.label ?? ''
  })
}

/**
 * Resolves the footer amount-to-next call-out for a ladder: the minimal "away from the next reward"
 * string, or `''` once all tiers are unlocked (no next tier → the footer hides it). Pure, so the
 * footer and harness share one implementation.
 */
export function buildFooterCallout(
  tiers: RewardTier[],
  rewardsTrackerModule: ContentfulContentModule | null
): string {
  const current = tiers.find((t) => t.state === RewardTierState.Current)
  if (!current) {
    return ''
  }
  const template =
    getTextContentItemValueFromContentModule(
      rewardsTrackerModule,
      FOOTER_CALLOUT.id,
      FOOTER_CALLOUT.fallback
    ) ?? FOOTER_CALLOUT.fallback
  return substitute(template, {
    AmountToNext: formatWholeDollarCurrency(current.amountToUnlock),
    NextRewardName: current.label
  })
}

export interface UseRewards {
  /** Proof-type-filtered, state-stamped reward tiers for the current cart subtotal. */
  tiers: ComputedRef<RewardTier[]>
  /** Fully resolved heading string for the current state (markdown). */
  heading: ComputedRef<string>
  /** Footer amount-to-next call-out (markdown); empty once all rewards are unlocked. */
  footerCallout: ComputedRef<string>
  /** ADR 003 first-tier detection — render the tracker when true, the legacy bar otherwise. */
  isExpanded: ComputedRef<boolean>
}

/**
 * Data layer for the `RewardsTracker`: assembles its presentational props from the catalog store
 * (tiers), the cart subtotal (`subtotalPostDiscounts`), and the `rewards-tracker` content module
 * (heading). The consuming surface supplies the `size` and decides — via `isExpanded` — whether to
 * render the tracker or the legacy `RewardsProgress` bar.
 */
export function useRewards(): UseRewards {
  const catalogStore = useCatalogStore()
  const cartStore = useCartStore()
  const appContentStore = useAppContentStore()

  const effectiveSubtotal = computed(() => cartStore.effectiveSubtotal)
  const tiers = computed(() => catalogStore.rewardTiers(effectiveSubtotal.value))
  const isExpanded = computed(() => catalogStore.isExpandedRewardLadder)
  const heading = computed(() =>
    buildRewardsHeading(tiers.value, effectiveSubtotal.value, appContentStore.rewardsTrackerContent)
  )
  const footerCallout = computed(() =>
    buildFooterCallout(tiers.value, appContentStore.rewardsTrackerContent)
  )

  return { tiers, heading, footerCallout, isExpanded }
}
